import React, { useEffect, useState, useMemo } from "react";
import { authClient, fetchCPA, updateCPA, fetchMetaAds, updateMetaSpend, fetchTravelAds, updateTravelAds, fetchVoiceWidgetReport } from "@/lib/adminApi";
import { toast } from "sonner";
import { TrendingUp, Users, Calendar, Mail, Activity, Loader2, Flame, Check, Scale, Save, Target, Trophy, Plane } from "lucide-react";

const SOURCE_LABEL = {
  contact_form: "Contato",
  newsletter: "Newsletter",
  lead_magnet_50_expressoes: "LM · 50 expressões",
  lead_magnet_a1_premiers_pas: "LM · A1",
  quiz_result: "Teste de nível",
  booking_form: "Agendamento",
  aula_gratis_lp: "LP aula grátis",
};

const SOURCE_COLOR = {
  contact_form: "#5c8aa6",
  newsletter: "#d9381e",
  lead_magnet_50_expressoes: "#7a4a8a",
  lead_magnet_a1_premiers_pas: "#9c9c9c",
  quiz_result: "#1e2a5a",
  booking_form: "#1e7a3e",
  aula_gratis_lp: "#e5b3b3",
};

const AdminMetrics = () => {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [streak, setStreak] = useState(null);
  const [loggingReel, setLoggingReel] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [m, s] = await Promise.all([
          authClient().get(`/admin/metrics?days=${days}`),
          authClient().get(`/admin/reel/streak`),
        ]);
        if (alive) {
          setData(m.data);
          setStreak(s.data);
        }
      } catch {
        if (alive) setData(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [days]);

  const handleLogReel = async () => {
    setLoggingReel(true);
    try {
      const res = await authClient().post(`/admin/reel/log`);
      setStreak(res.data);
      toast.success(
        res.data.logged_today
          ? `🔥 Streak: ${res.data.current_streak} dias`
          : "Reel de hoje registrado!"
      );
    } catch {
      toast.error("Não deu certo. Tenta de novo?");
    } finally {
      setLoggingReel(false);
    }
  };

  const maxDaily = useMemo(() => {
    if (!data?.daily) return 1;
    return Math.max(1, ...data.daily.map((d) => d.total));
  }, [data]);

  const sortedSources = useMemo(() => {
    if (!data?.by_source_all_time) return [];
    return Object.entries(data.by_source_all_time)
      .map(([k, v]) => ({
        key: k,
        label: SOURCE_LABEL[k] || k,
        count: v,
        color: SOURCE_COLOR[k] || "#3a3a3a",
      }))
      .sort((a, b) => b.count - a.count);
  }, [data]);

  const totalAllTime = sortedSources.reduce((acc, s) => acc + s.count, 0);

  if (loading || !data) {
    return (
      <div className="mt-16 flex flex-col items-center gap-3 text-[#5c5c5c]">
        <Loader2 className="h-5 w-5 animate-spin" />
        <p className="text-sm">Carregando métricas…</p>
      </div>
    );
  }

  return (
    <div className="mt-8 space-y-6" data-testid="admin-metrics-tab">
      {/* Reel Streak card */}
      <div
        data-testid="reel-streak-card"
        className="bg-gradient-to-br from-[#d9381e] to-[#c02f16] text-[#f4f1ea] rounded-xl p-5 sm:p-6 flex flex-wrap items-center gap-4 justify-between"
      >
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 rounded-full bg-[#f4f1ea]/15 flex items-center justify-center">
            <Flame
              className={`h-7 w-7 ${
                streak?.current_streak > 0 ? "text-[#f4f1ea]" : "text-[#f4f1ea]/40"
              }`}
              strokeWidth={2}
            />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#f4f1ea]/85">
              Reel streak
            </p>
            <p className="font-display text-3xl leading-none mt-1">
              {streak?.current_streak ?? "—"}{" "}
              <span className="text-base font-normal">dias</span>
            </p>
            <p className="text-xs text-[#f4f1ea]/70 mt-1">
              {streak?.total_posted ?? 0} reels registrados no total
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleLogReel}
          disabled={loggingReel || streak?.logged_today}
          data-testid="log-reel-btn"
          className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
            streak?.logged_today
              ? "bg-[#f4f1ea]/20 text-[#f4f1ea]/70 cursor-not-allowed"
              : "bg-[#f4f1ea] text-[#d9381e] hover:bg-[#f4f1ea]/90"
          }`}
        >
          {loggingReel ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : streak?.logged_today ? (
            <>
              <Check className="h-4 w-4" strokeWidth={2.5} />
              Feito hoje
            </>
          ) : (
            <>
              <Flame className="h-4 w-4" strokeWidth={2.5} />
              Registrar Reel de hoje
            </>
          )}
        </button>
      </div>

      {/* Range switch */}
      <div className="flex items-center gap-2">
        <span className="text-xs uppercase tracking-[0.18em] text-[#5c5c5c]">Período</span>
        {[7, 30, 90].map((d) => (
          <button
            key={d}
            onClick={() => setDays(d)}
            data-testid={`metrics-range-${d}`}
            className={`rounded-full px-3 py-1 text-xs transition-colors border ${
              days === d
                ? "bg-[#1e2a5a] text-[#f4f1ea] border-[#1e2a5a]"
                : "border-[#e5e3db] text-[#5c5c5c] hover:border-[#1e2a5a] hover:text-[#1e2a5a]"
            }`}
          >
            {d}d
          </button>
        ))}
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI Icon={Users} label="Leads no período" value={data.total_leads} color="#1e2a5a" testid="kpi-leads" />
        <KPI Icon={Calendar} label="Agendamentos" value={data.total_bookings} color="#1e7a3e" testid="kpi-bookings" />
        <KPI Icon={Mail} label="Newsletter" value={data.total_newsletter} color="#d9381e" testid="kpi-newsletter" />
        <KPI Icon={TrendingUp} label="Top source" value={SOURCE_LABEL[data.top_source] || data.top_source || "—"} color="#7a4a8a" small testid="kpi-top" />
      </div>

      {/* Daily leads chart */}
      <div className="bg-white border border-[#e5e3db] rounded-xl p-5 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <p className="font-display text-lg text-[#1e2a5a]">Leads por dia · últimos {days}d</p>
          <Activity className="h-4 w-4 text-[#5c5c5c]" />
        </div>
        <div className="flex items-end gap-1 h-40" data-testid="metrics-daily-chart">
          {data.daily.map((point) => {
            const heightPct = maxDaily > 0 ? (point.total / maxDaily) * 100 : 0;
            const day = point.date.slice(-5); // MM-DD
            const title = `${point.date} · ${point.total} lead(s)`;
            return (
              <div
                key={point.date}
                title={title}
                className="flex-1 flex flex-col items-center justify-end group"
              >
                <div
                  className={`w-full rounded-t-sm transition-all group-hover:opacity-80 ${
                    point.total === 0 ? "bg-[#e5e3db]" : "bg-[#1e2a5a]"
                  }`}
                  style={{ height: `${Math.max(heightPct, 2)}%` }}
                />
                {days <= 30 && (
                  <span className="mt-1 text-[9px] text-[#5c5c5c] rotate-45 origin-left translate-y-2">
                    {day}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Sources ranking */}
      <div className="bg-white border border-[#e5e3db] rounded-xl p-5 sm:p-6">
        <p className="font-display text-lg text-[#1e2a5a] mb-4">
          Origem dos leads · TODOS os tempos ({totalAllTime})
        </p>
        <div className="space-y-3" data-testid="metrics-sources-bar">
          {sortedSources.map((s) => {
            const pct = totalAllTime > 0 ? (s.count / totalAllTime) * 100 : 0;
            return (
              <div key={s.key}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-[#3a3a3a]">{s.label}</span>
                  <span className="text-[#5c5c5c]">
                    {s.count} · {pct.toFixed(1)}%
                  </span>
                </div>
                <div className="h-2 rounded-full bg-[#f0efea] overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${pct}%`, backgroundColor: s.color }}
                  />
                </div>
              </div>
            );
          })}
          {sortedSources.length === 0 && (
            <p className="text-sm text-[#5c5c5c] italic">Sem dados ainda.</p>
          )}
        </div>
      </div>

      <CPAPanel />
      <MetaAdsPanel />
      <TravelAdsPanel />
      <VoiceWidgetAbPanel />
    </div>
  );
};

const brl = (v) =>
  v == null
    ? "—"
    : `R$ ${v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CPAPanel = () => {
  const [cfg, setCfg] = useState(null);
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetchCPA();
        setCfg(r.config);
        setResult(r);
      } catch {
        setCfg({
          ads_monthly_spend_brl: 0,
          ads_leads_per_month: 0,
          reels_hours_per_week: 0,
          reels_hour_rate_brl: 0,
          reels_leads_per_month: 0,
          conversion_rate_pct: 20,
        });
      }
    })();
  }, []);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...cfg,
        ads_monthly_spend_brl: Number(cfg.ads_monthly_spend_brl) || 0,
        ads_leads_per_month: Number(cfg.ads_leads_per_month) || 0,
        reels_hours_per_week: Number(cfg.reels_hours_per_week) || 0,
        reels_hour_rate_brl: Number(cfg.reels_hour_rate_brl) || 0,
        reels_leads_per_month: Number(cfg.reels_leads_per_month) || 0,
        conversion_rate_pct: Number(cfg.conversion_rate_pct) || 20,
      };
      const r = await updateCPA(payload);
      setResult(r);
      setCfg(r.config);
      toast.success("Comparação atualizada.");
    } catch {
      toast.error("Não deu certo. Tenta de novo?");
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return null;

  const winner = result?.winner;
  const savingsPct = result?.savings_pct;

  return (
    <div
      data-testid="cpa-panel"
      className="bg-white border border-[#e5e3db] rounded-xl p-5 sm:p-6"
    >
      <div className="flex items-center gap-2 text-[#1e2a5a]">
        <Scale className="h-5 w-5" strokeWidth={1.75} />
        <p className="text-sm font-semibold">CPA · Google Ads vs Reels orgânicos</p>
      </div>
      <p className="mt-2 text-xs text-[#5c5c5c]">
        Preencha os campos abaixo. Fórmula: <em>CPA = (custo do canal) / (leads × taxa de conversão em aluno)</em>.
      </p>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        {/* GOOGLE ADS COLUMN */}
        <div className="rounded-lg border border-[#e5e3db] p-4 space-y-3">
          <p className="text-xs uppercase tracking-[0.18em] text-[#5c5c5c] font-semibold">
            Google Ads
          </p>
          <CPAInput
            label="Investimento mensal (R$)"
            value={cfg.ads_monthly_spend_brl}
            onChange={(v) => set("ads_monthly_spend_brl", v)}
            testid="cpa-ads-spend"
          />
          <CPAInput
            label="Leads por mês vindos de Ads"
            value={cfg.ads_leads_per_month}
            onChange={(v) => set("ads_leads_per_month", v)}
            testid="cpa-ads-leads"
            integer
          />
        </div>
        {/* REELS COLUMN */}
        <div className="rounded-lg border border-[#e5e3db] p-4 space-y-3">
          <p className="text-xs uppercase tracking-[0.18em] text-[#5c5c5c] font-semibold">
            Reels orgânicos
          </p>
          <CPAInput
            label="Horas por semana produzindo"
            value={cfg.reels_hours_per_week}
            onChange={(v) => set("reels_hours_per_week", v)}
            testid="cpa-reels-hours"
          />
          <CPAInput
            label="Valor da sua hora (R$)"
            value={cfg.reels_hour_rate_brl}
            onChange={(v) => set("reels_hour_rate_brl", v)}
            testid="cpa-reels-rate"
          />
          <CPAInput
            label="Leads por mês vindos de Reels"
            value={cfg.reels_leads_per_month}
            onChange={(v) => set("reels_leads_per_month", v)}
            testid="cpa-reels-leads"
            integer
          />
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-4">
        <CPAInput
          label="Taxa de conversão lead → aluno (%)"
          value={cfg.conversion_rate_pct}
          onChange={(v) => set("conversion_rate_pct", v)}
          testid="cpa-conversion"
          className="w-56"
        />
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="cpa-save"
          className="inline-flex items-center gap-2 rounded-full bg-[#d9381e] px-5 py-2 text-sm font-semibold text-[#f4f1ea] hover:bg-[#1e2a5a] transition-colors disabled:opacity-60"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          Calcular e salvar
        </button>
      </div>

      {result && (
        <div className="mt-6 grid gap-3 sm:grid-cols-2" data-testid="cpa-result">
          <div className="rounded-lg border-2 p-4"
               style={{
                 borderColor: winner === "ads" ? "#1e7a3e" : "#e5e3db",
                 background: winner === "ads" ? "#e8f4ea" : "#fff",
               }}>
            <p className="text-xs uppercase tracking-[0.18em] text-[#5c5c5c]">Google Ads</p>
            <p className="mt-2 font-display text-2xl text-[#1e2a5a]" data-testid="cpa-ads-cpa">
              {brl(result.ads_cost_per_student)}<span className="text-sm font-normal text-[#5c5c5c]"> / aluno</span>
            </p>
            <p className="text-xs text-[#5c5c5c] mt-1">
              CPL: {brl(result.ads_cost_per_lead)}
            </p>
            {winner === "ads" && (
              <p className="mt-2 text-xs font-semibold text-[#1e7a3e]">
                🏆 {savingsPct}% mais barato que Reels
              </p>
            )}
          </div>
          <div className="rounded-lg border-2 p-4"
               style={{
                 borderColor: winner === "reels" ? "#1e7a3e" : "#e5e3db",
                 background: winner === "reels" ? "#e8f4ea" : "#fff",
               }}>
            <p className="text-xs uppercase tracking-[0.18em] text-[#5c5c5c]">Reels orgânicos</p>
            <p className="mt-2 font-display text-2xl text-[#1e2a5a]" data-testid="cpa-reels-cpa">
              {brl(result.reels_cost_per_student)}<span className="text-sm font-normal text-[#5c5c5c]"> / aluno</span>
            </p>
            <p className="text-xs text-[#5c5c5c] mt-1">
              CPL: {brl(result.reels_cost_per_lead)}
            </p>
            {winner === "reels" && (
              <p className="mt-2 text-xs font-semibold text-[#1e7a3e]">
                🏆 {savingsPct}% mais barato que Ads
              </p>
            )}
          </div>
          {winner === "tie" && (
            <p className="sm:col-span-2 text-sm text-[#5c5c5c] italic text-center">
              Empate técnico — os dois canais custam o mesmo por aluno.
            </p>
          )}
          {!winner && (
            <p className="sm:col-span-2 text-sm text-[#5c5c5c] italic">
              Preencha os dois canais para ver a comparação e a economia.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const CPAInput = ({ label, value, onChange, testid, integer, className }) => (
  <label className={`block ${className || ""}`}>
    <span className="text-xs uppercase tracking-[0.15em] text-[#5c5c5c]">
      {label}
    </span>
    <input
      type="number"
      min="0"
      step={integer ? "1" : "0.01"}
      value={value ?? 0}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="mt-1 w-full rounded-lg border border-[#e5e3db] px-3 py-2 text-sm bg-white focus:border-[#d9381e] focus:outline-none focus:ring-2 focus:ring-[#d9381e]/15"
    />
  </label>
);

const TRAVEL_AD_COPY = {
  primary_pt: "🇫🇷 Vai pra Paris nos próximos 3 meses?\n\n10 frases GRÁTIS pra chegar destravado no Charles de Gaulle. Áudio narrado pelo Prof Lionel, parisiense de verdade.\n\nBaixe agora (30 seg pra pedir) 👇",
  headline: "Guia CDG grátis · Prof Lionel",
  cta: "Baixar guia",
};

const TravelAdsPanel = () => {
  const [report, setReport] = useState(null);
  const [spend, setSpend] = useState({ total_spend_brl: 0, days_active: 7 });
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const r = await fetchTravelAds();
      setReport(r);
      setSpend({
        total_spend_brl: r.spend.total_spend_brl || 0,
        days_active: r.spend.days_active || 7,
      });
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await updateTravelAds({
        total_spend_brl: Number(spend.total_spend_brl) || 0,
        days_active: Number(spend.days_active) || 7,
      });
      setReport(r);
      toast.success("CPA pré-viagem recalculado.");
    } catch {
      toast.error("Falha ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  const copyCopy = async () => {
    try {
      await navigator.clipboard.writeText(TRAVEL_AD_COPY.primary_pt);
      setCopied(true);
      toast.success("Copy do anúncio copiada!");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Não deu pra copiar.");
    }
  };

  if (!report) return null;

  const g = report.google_ads_cpa;
  const beats = report.beats_google_ads;

  return (
    <div
      data-testid="travel-ads-panel"
      className="bg-white border border-[#e5e3db] rounded-xl p-5 sm:p-6"
    >
      <div className="flex items-center gap-2 text-[#1e2a5a]">
        <Plane className="h-5 w-5" strokeWidth={1.75} />
        <p className="text-sm font-semibold">Meta Ads Pré-viagem · <code>/paris-em-10-frases</code></p>
      </div>
      <p className="mt-2 text-xs text-[#5c5c5c]">
        Campanha única (R$ 5/dia × 7 dias = R$ 35). Público: brasileiros
        planejando viagem à França nos próximos 90 dias. Leads contados
        automaticamente por <code>source=paris_em_10_frases</code>.
      </p>

      <div className="mt-5 grid gap-4 sm:grid-cols-3">
        <CPAInput
          label="R$ total investido"
          value={spend.total_spend_brl}
          onChange={(v) => setSpend((s) => ({ ...s, total_spend_brl: v }))}
          testid="travel-spend"
        />
        <CPAInput
          label="Dias ativos"
          value={spend.days_active}
          onChange={(v) => setSpend((s) => ({ ...s, days_active: v }))}
          testid="travel-days"
          integer
        />
        <div className="flex items-end">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="travel-save"
            className="inline-flex items-center gap-2 rounded-full bg-[#d9381e] px-5 py-2 text-sm font-semibold text-[#f4f1ea] hover:bg-[#1e2a5a] transition-colors disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Calcular
          </button>
        </div>
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        <div className="rounded-lg border border-[#e5e3db] p-4">
          <p className="text-xs uppercase tracking-[0.15em] text-[#5c5c5c]">Leads capturados</p>
          <p className="mt-2 font-display text-2xl text-[#1e2a5a]" data-testid="travel-leads">
            {report.leads}
          </p>
        </div>
        <div className="rounded-lg border border-[#e5e3db] p-4">
          <p className="text-xs uppercase tracking-[0.15em] text-[#5c5c5c]">CPL (por lead)</p>
          <p className="mt-2 font-display text-2xl text-[#1e2a5a]" data-testid="travel-cpl">
            {report.cpl != null ? `R$ ${report.cpl.toFixed(2)}` : "—"}
          </p>
        </div>
        <div className={`rounded-lg border-2 p-4 ${beats ? "border-[#1e7a3e] bg-[#e8f4ea]" : "border-[#e5e3db]"}`}>
          <p className="text-xs uppercase tracking-[0.15em] text-[#5c5c5c]">CPA (por aluno)</p>
          <p className="mt-2 font-display text-2xl text-[#1e2a5a]" data-testid="travel-cpa">
            {report.cpa != null ? `R$ ${report.cpa.toFixed(2)}` : "—"}
          </p>
          {report.cpa != null && g != null && (
            <p className={`mt-1 text-xs font-semibold ${beats ? "text-[#1e7a3e]" : "text-[#5c5c5c]"}`}>
              vs Google Ads R$ {g.toFixed(2)}{" "}
              {beats ? `· 🏆 -${report.savings_pct}%` : "· 🔴 acima"}
            </p>
          )}
        </div>
      </div>

      <details className="mt-6 text-sm text-[#5c5c5c]" data-testid="travel-launch-guide">
        <summary className="cursor-pointer hover:text-[#1e2a5a] font-medium">
          📖 Guia de lançamento · R$ 35 total · público « viaja pra França »
        </summary>
        <div className="mt-4 space-y-4">
          <div>
            <p className="font-semibold text-[#1e2a5a] mb-2">1. Configuração básica</p>
            <ul className="list-disc ml-5 space-y-1 leading-relaxed">
              <li>Business Manager → « Nova campanha » → Objetivo <strong>« Cadastros »</strong>.</li>
              <li>Orçamento diário: <strong>R$ 5</strong>. Duração: <strong>7 dias</strong>. Total: R$ 35.</li>
              <li>Formato: <strong>vídeo curto vertical (9:16, 15s)</strong> mostrando você falando + legenda em PT.</li>
            </ul>
          </div>

          <div>
            <p className="font-semibold text-[#1e2a5a] mb-2">2. Público (viaja pra França em 90d)</p>
            <ul className="list-disc ml-5 space-y-1 leading-relaxed">
              <li><strong>Local</strong>: Brasil (todo).</li>
              <li><strong>Idade</strong>: 25-55.</li>
              <li><strong>Comportamento</strong>: « Frequent travelers » + « Recently returned from a trip 90+ days » (Meta detecta pelo device location e apps).</li>
              <li><strong>Interesses</strong>: França · Paris · Torre Eiffel · Louvre · Air France · Viagem internacional · Passaporte.</li>
              <li><strong>Excluir</strong>: quem já é lead (upload CSV do admin) + países fora do Brasil.</li>
            </ul>
          </div>

          <div>
            <p className="font-semibold text-[#1e2a5a] mb-2">3. URL de destino + UTMs</p>
            <code className="block bg-[#f0efea] p-3 rounded text-xs break-all">
              https://npdlbr.com.br/paris-em-10-frases?utm_source=meta&utm_medium=cpc&utm_campaign=preTrip&utm_content=video_pt
            </code>
          </div>

          <div>
            <p className="font-semibold text-[#1e2a5a] mb-2">4. Copy pronta (PT)</p>
            <div className="bg-[#f0efea] p-3 rounded text-xs whitespace-pre-line leading-relaxed">
              {TRAVEL_AD_COPY.primary_pt}
            </div>
            <p className="mt-2 text-[11px] text-[#5c5c5c]">
              <strong>Headline</strong>: {TRAVEL_AD_COPY.headline} · <strong>CTA button</strong>: {TRAVEL_AD_COPY.cta}
            </p>
            <button
              type="button"
              onClick={copyCopy}
              data-testid="travel-copy-btn"
              className="mt-3 inline-flex items-center gap-2 rounded-full border border-[#1e2a5a] px-4 py-1.5 text-xs text-[#1e2a5a] hover:bg-[#1e2a5a] hover:text-[#f4f1ea] transition-colors"
            >
              {copied ? <Check className="h-3 w-3" strokeWidth={2} /> : <Target className="h-3 w-3" strokeWidth={2} />}
              {copied ? "Copiado!" : "Copiar copy"}
            </button>
          </div>

          <div>
            <p className="font-semibold text-[#1e2a5a] mb-2">5. Criativo do vídeo (15s)</p>
            <ol className="list-decimal ml-5 space-y-1 leading-relaxed">
              <li><strong>0-3s</strong>: você olhando a câmera « Vai pra Paris? »</li>
              <li><strong>3-8s</strong>: cortes rápidos das 3 frases mais úteis (« Bonjour », « Combien pour aller au centre? », « Où sont les toilettes? »)</li>
              <li><strong>8-12s</strong>: card do post « 10 frases grátis · voz do Prof Lionel »</li>
              <li><strong>12-15s</strong>: CTA « Baixa no link · leva 30 seg »</li>
            </ol>
          </div>

          <div className="bg-[#1e2a5a] text-[#f4f1ea] p-4 rounded-lg">
            <p className="font-semibold mb-2">📊 Meta CPA</p>
            <p className="text-xs">
              Como a LP <code>/paris-em-10-frases</code> tem intent MUITO alta (pré-viagem), o CPL esperado é
              <strong> R$ 3-6</strong> (vs R$ 8-15 em campanhas genéricas). CPA esperado: <strong>R$ 30-60 por aluno</strong>{" "}
              (5-8× melhor que Google Ads R$ 250). Se em 7 dias você não bater R$ 100 CPA, otimize o vídeo (não o público).
            </p>
          </div>
        </div>
      </details>
    </div>
  );
};

const VARIANT_LABELS = {
  a: { label: "A · Humano", tag: "parisiense de verdade" },
  b: { label: "B · Resultado", tag: "falar em 30 dias" },
  c: { label: "C · Prova social", tag: "500+ brasileiros" },
};

const MetaAdsPanel = () => {
  const [report, setReport] = useState(null);
  const [spend, setSpend] = useState({ a: 0, b: 0, c: 0 });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await fetchMetaAds();
      setReport(r);
      setSpend({
        a: r.spend.a || 0,
        b: r.spend.b || 0,
        c: r.spend.c || 0,
      });
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await updateMetaSpend({
        a: Number(spend.a) || 0,
        b: Number(spend.b) || 0,
        c: Number(spend.c) || 0,
      });
      setReport(r);
      toast.success("Investimento atualizado — CPA recalculado.");
    } catch {
      toast.error("Não deu pra salvar.");
    } finally {
      setSaving(false);
    }
  };

  if (!report) return null;

  const winner = report.winner_variant;
  const winnerCPA = report.winner_cpa;
  const google = report.google_ads_cpa;

  return (
    <div
      data-testid="meta-ads-panel"
      className="bg-white border border-[#e5e3db] rounded-xl p-5 sm:p-6"
    >
      <div className="flex items-center gap-2 text-[#1e2a5a]">
        <Target className="h-5 w-5" strokeWidth={1.75} />
        <p className="text-sm font-semibold">Meta Ads Micro-Test · /aula-gratis A/B/C</p>
      </div>
      <p className="mt-2 text-xs text-[#5c5c5c]">
        Preencha o R$ investido em cada variante. Leads são contados
        automaticamente pelo <code>source</code> do formulário
        (<code>aula_gratis_lp_a</code> / <code>_b</code> / <code>_c</code>).
      </p>

      <div className="mt-5 grid gap-3 sm:grid-cols-3">
        {["a", "b", "c"].map((v) => (
          <div
            key={v}
            data-testid={`meta-variant-${v}`}
            className={`rounded-lg border-2 p-4 space-y-2 ${
              winner === v ? "border-[#1e7a3e] bg-[#e8f4ea]" : "border-[#e5e3db]"
            }`}
          >
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-[0.15em] text-[#5c5c5c] font-semibold">
                {VARIANT_LABELS[v].label}
              </p>
              {winner === v && <Trophy className="h-4 w-4 text-[#1e7a3e]" />}
            </div>
            <p className="text-[11px] italic text-[#5c5c5c]">
              « {VARIANT_LABELS[v].tag} »
            </p>
            <CPAInput
              label="R$ investido"
              value={spend[v]}
              onChange={(val) => setSpend((s) => ({ ...s, [v]: val }))}
              testid={`meta-spend-${v}`}
            />
            <div className="grid grid-cols-2 gap-2 pt-1 text-xs">
              <div>
                <p className="text-[10px] uppercase tracking-[0.15em] text-[#5c5c5c]">
                  Leads
                </p>
                <p
                  className="mt-0.5 font-display text-lg text-[#1e2a5a]"
                  data-testid={`meta-leads-${v}`}
                >
                  {report.leads[v] ?? 0}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.15em] text-[#5c5c5c]">
                  CPA
                </p>
                <p
                  className="mt-0.5 font-display text-lg text-[#1e2a5a]"
                  data-testid={`meta-cpa-${v}`}
                >
                  {report.cpa[v] != null
                    ? `R$ ${report.cpa[v].toFixed(2)}`
                    : "—"}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="meta-save"
          className="inline-flex items-center gap-2 rounded-full bg-[#d9381e] px-5 py-2 text-sm font-semibold text-[#f4f1ea] hover:bg-[#1e2a5a] transition-colors disabled:opacity-60"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          Salvar e recalcular
        </button>
        <button
          type="button"
          onClick={load}
          className="text-xs link-underline text-[#5c5c5c] hover:text-[#1e2a5a]"
        >
          Atualizar leads
        </button>
      </div>

      {winner && (
        <div
          className="mt-5 rounded-lg bg-[#1e2a5a] text-[#f4f1ea] p-4"
          data-testid="meta-verdict"
        >
          <p className="text-xs uppercase tracking-[0.18em] text-[#e5b3b3]">
            Veredito
          </p>
          <p className="mt-2 font-display text-2xl">
            🏆 Variante {winner.toUpperCase()} vence com R$ {winnerCPA.toFixed(2)} / aluno
          </p>
          {google != null && (
            <p className="mt-2 text-sm text-[#f4f1ea]/85">
              {report.beats_google_ads ? (
                <>
                  ✅ Meta bate Google Ads (R$ {google.toFixed(2)}) por{" "}
                  <strong>{(((google - winnerCPA) / google) * 100).toFixed(1)}%</strong>.
                  Escalar orçamento Meta.
                </>
              ) : (
                <>
                  ❌ Meta ainda perde pra Google Ads (R$ {google.toFixed(2)}).
                  Otimizar creative da variante {winner.toUpperCase()} antes de escalar.
                </>
              )}
            </p>
          )}
        </div>
      )}

      <details className="mt-4 text-sm text-[#5c5c5c]">
        <summary className="cursor-pointer hover:text-[#1e2a5a] font-medium">
          📖 Como lançar as 3 campanhas Meta (guia rápido · R$ 105 total)
        </summary>
        <ol className="mt-3 space-y-2 list-decimal ml-5 leading-relaxed">
          <li>
            <strong>Business Manager</strong> → Criar 1 conta de anúncios BRL
            (business.facebook.com).
          </li>
          <li>
            <strong>3 conjuntos de anúncios</strong>, 1 por variante. Objetivo:
            « Cadastros/Leads ». Orçamento <strong>R$ 5/dia por conjunto</strong>{" "}
            (total R$ 15/dia × 7 dias = <strong>R$ 105</strong>).
          </li>
          <li>
            <strong>Público</strong>: Brasil · 25-55 anos · interesses = francês
            + Paris + DELF + viagem França. Exclua quem já é lead.
          </li>
          <li>
            <strong>URL de destino</strong>:
            <br />
            <code className="text-xs">https://npdlbr.com.br/aula-gratis?v=a</code>
            <br />
            <code className="text-xs">https://npdlbr.com.br/aula-gratis?v=b</code>
            <br />
            <code className="text-xs">https://npdlbr.com.br/aula-gratis?v=c</code>
            <br />
            Isso força a variante correta pra cada campanha (o parâmetro
            <code>?v=</code> sobrescreve o split A/B/C aleatório).
          </li>
          <li>
            <strong>UTM tag</strong>:
            <code className="text-xs ml-1">
              ?v=&#123;variant&#125;&amp;utm_source=meta&amp;utm_campaign=micro_test
            </code>
          </li>
          <li>
            <strong>Rodar 7 dias</strong> sem tocar. Voltar aqui, atualizar os
            R$ gastos por variante → CPA por variante aparece automaticamente.
          </li>
          <li>
            <strong>Decidir</strong>: se algum CPA fica abaixo de R${" "}
            {google != null ? google.toFixed(0) : "250"} (Google Ads), escalar
            aquela variante <em>gradualmente</em> (R$ 5 → R$ 10 → R$ 20/dia).
            Se nenhuma bate, otimizar creative + público.
          </li>
        </ol>
        <p className="mt-3 text-xs italic">
          💡 R$ 5/dia é o mínimo Meta pra learning phase. Não escale antes de
          bater o CPA do Google — algoritmo precisa 7 dias pra otimizar.
        </p>
      </details>
    </div>
  );
};

const KPI = ({ Icon, label, value, color, small, testid }) => (
  <div
    data-testid={testid}
    className="bg-white border border-[#e5e3db] rounded-xl p-4"
  >
    <div className="flex items-center gap-2 text-[#5c5c5c]">
      <Icon className="h-4 w-4" strokeWidth={1.75} style={{ color }} />
      <p className="text-[10px] uppercase tracking-[0.18em]">{label}</p>
    </div>
    <p
      className={`mt-3 font-display leading-none ${small ? "text-lg" : "text-3xl"} text-[#1e2a5a]`}
    >
      {value}
    </p>
  </div>
);

export default AdminMetrics;

// ===== Voice Widget A/B panel =====
const VoiceWidgetAbPanel = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const data = await fetchVoiceWidgetReport();
        if (alive) setReport(data);
      } catch (_e) {
        void _e;
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    const id = setInterval(load, 30000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  if (loading) return null;
  if (!report) return null;

  const winnerLabel = {
    a: "🅰️ Calmo venceu",
    b: "🅱️ Energético venceu",
    tie: "Empate técnico",
    insufficient_data: "Dados insuficientes (< 50 impressões)",
  }[report.winner] || "—";

  return (
    <div
      data-testid="voice-widget-ab-panel"
      className="mt-6 rounded-2xl border border-[#e5e3db] bg-white p-4 sm:p-6"
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#d9381e] font-medium">
            A/B — Voice Widget
          </p>
          <h3 className="mt-1 font-display text-xl text-[#1e2a5a]">
            Hook: calmo vs energético
          </h3>
        </div>
        <span
          data-testid="voice-widget-ab-winner"
          className="text-xs rounded-full border border-[#e5e3db] px-3 py-1 text-[#1e2a5a]"
        >
          {winnerLabel}
        </span>
      </div>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <AbCard variant="a" title="Calmo (professor)" data={report.a} />
        <AbCard variant="b" title="Energético (hook)" data={report.b} />
      </div>
      <p className="mt-3 text-xs text-[#5c5c5c]">
        Métricas coletadas em tempo real via <code>/api/ab/voice-widget</code>. Winner definido pela taxa de clique no CTA acima de 50 impressões.
      </p>
    </div>
  );
};

const AbCard = ({ variant, title, data }) => (
  <div
    data-testid={`ab-card-${variant}`}
    className="rounded-xl border border-[#e5e3db] bg-[#faf9f4] p-4"
  >
    <p className="text-[10px] uppercase tracking-[0.18em] text-[#5c5c5c]">
      Variant {variant.toUpperCase()}
    </p>
    <h4 className="mt-1 font-display text-lg text-[#1e2a5a]">{title}</h4>
    <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
      <AbStat label="Impressões" value={data.impressions} />
      <AbStat label="Plays" value={data.plays} />
      <AbStat label="Completos" value={data.completes} />
      <AbStat label="CTA cliques" value={data.cta_clicks} />
      <AbStat label="Play rate" value={`${data.play_rate}%`} accent />
      <AbStat label="CTA rate" value={`${data.cta_rate}%`} accent />
    </div>
  </div>
);

const AbStat = ({ label, value, accent }) => (
  <div className="flex flex-col">
    <span className="text-[10px] uppercase tracking-[0.16em] text-[#5c5c5c]">{label}</span>
    <span className={`mt-0.5 font-medium ${accent ? "text-[#d9381e]" : "text-[#1e2a5a]"}`}>
      {value}
    </span>
  </div>
);
